// The Calendar page (U91, U95, U97, U98, U99, U100). Mike: "calendar should be a page on its own
// within the top 4 menu items… it is intuitive to the working flow, and allows us to preview each
// meeting with the co-pilot", and "Just like an outlook calendar, except when you click into it, you
// get the full co-pilot meeting brief etc."
//
// So: a week anyone who has used Outlook can read — Monday to Sunday, hours down the side, each meeting
// a block in its day — drawn in this look's grammar rather than Outlook's chrome. A meeting with a
// client outside the company is the one a glance should find, so it carries the client's name and the
// lead treatment; everything else is quiet. A block whose words Cobrain does not keep says "Busy",
// which is the honest thing to draw (R42).
//
// Clicking one opens the brief beside the week, without leaving the page.
//
// Five things the audit of 2026-09-20 changed, each measured on his own store rather than guessed:
//   * It opens on the week holding the next meeting still to start (U97). It used to open on the
//     calendar week, so on a Sunday evening — when the night-before read is actually done — it showed
//     six days that were already over.
//   * The grid draws all twenty-four hours and scrolls, opening at 07:00 (U98). It used to draw 07:00
//     to 20:00 and put anything later at the foot of the column, where `overflow: hidden` removed it.
//   * A block too short to stack its lines puts the time and the title on one line (`isShort`). Seven
//     of his fourteen blocks were 15px slivers showing a clock time with the name clipped away.
//   * Every meeting says whether Cobrain will record it (U99), from the bot lane's own row.
//   * A private event is marked as triage marks one (U100, D77).
//
// On a phone the week genuinely becomes the days one under another (`WeekList`). The stylesheet used
// to say so in a comment while the media query changed nothing, and 10 of 14 titles were clipped
// inside 41px columns.

import { ActionButton } from '../parts/ActionButton.js';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLiveRefresh } from '../live.js';
import { Unauthenticated, type Store } from '../api.js';
import { calendar, type Brief, type WeekEvent } from './api.js';
import { fetchRundown, type RundownView } from '../company/api.js';
import { CompanyLine, CompanyName, inPortal } from '../parts/CompanyLine.js';
import { useCompanyLines } from '../company/lines.js';
import { Rundown } from '../company/Rundown.js';
import {
  allDayOf, captureWords, clock, dayAhead, dayDate, dayWords, DAY_PX, FIRST_HOUR, HOUR_PX, isShort, LAST_HOUR,
  blockRoom, marksTheBlock, mondayOf, NO_WORDS, nothingToBriefWords, openAtHour, openingMonday, place, spanWords, weekCount, weekWords,
} from './week.js';
import './calendar.css';
import { FilterBar, matchesWords } from '../parts/FilterBar.js';
import { useFilterParams } from '../find/params.js';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const LONG_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** The lock triage draws on a private item (D77, ItemCard.tsx), so the two surfaces mark it the same way. */
const Lock = () => (
  <svg className="cal-lock" viewBox="0 0 12 12" aria-hidden="true">
    <path d="M3.5 5V3.8a2.5 2.5 0 015 0V5h.5a1 1 0 011 1v4a1 1 0 01-1 1H3a1 1 0 01-1-1V6a1 1 0 011-1zm1 0h3V3.8a1.5 1.5 0 00-3 0z" />
  </svg>
);

/**
 * Give the two columns exactly the window that is left under everything above them.
 *
 * The page does not scroll on this route -- each column does -- and that only holds if the wrap is as
 * tall as the room it actually has. A constant in the stylesheet is a guess about the height of the
 * bar, the page's padding and the week's own head, and it was wrong by enough to leave the page
 * scrolling anyway. This measures it and follows a resize.
 */
function useFitsTheWindow(ref: React.RefObject<HTMLDivElement | null>, on: boolean): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;
    if (!on) { el.style.removeProperty('--cal-chrome'); return; }
    const fit = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      // The page reserves room under its content -- 96px of it, for the ask bar -- and the grid has no
      // business growing into it. Read rather than assumed: a constant here was wrong by exactly that
      // padding and left the page scrolling anyway, which is the thing this exists to stop.
      const parent = el.parentElement;
      const below = parent ? Number.parseFloat(window.getComputedStyle(parent).paddingBottom) || 0 : 0;
      el.style.setProperty('--cal-chrome', `${Math.round(top + below)}px`);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  });
}

/** True while the window is narrower than `q`, and it follows a resize. */
function useNarrow(q = '(max-width: 760px)'): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const m = window.matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [q]);
  return narrow;
}

/** Everything a screen reader needs about one meeting, in the order a person would say it. */
function labelOf(e: WeekEvent, dayName?: string): string {
  const parts = [dayName, spanWords(e.startsAt, e.endsAt), e.title ?? NO_WORDS];
  if (e.company) parts.push(`with ${e.company.name}`);
  if (e.private) parts.push('private, only you');
  parts.push(captureWords(e.capture).long);
  return parts.filter(Boolean).join(', ');
}

/** The find's one parameter in the address (pipeline F5). */
const FIND = ['q'] as const;

export function CalendarPage({ store }: { store: Store }) {
  const now = useMemo(() => new Date(), []);
  const narrow = useNarrow();
  // At 1200px and up an open meeting's rundown takes the page beside the week as a day list (pipeline RC5; drawing
  // l1-line-and-rundown.html, "settled by this drawing, reversible"). Reverse: drop `reading` and its two rules.
  const roomy = useNarrow('(min-width: 1200px)');
  const wrap = useRef<HTMLDivElement>(null);
  useFitsTheWindow(wrap, !narrow);

  // One read of the days from now: it decides which week the page opens on (U97) and it is what the
  // day-ahead panel shows, so the page does not ask for the same seven days twice.
  const [ahead, setAhead] = useState<WeekEvent[] | null>(null);
  const [aheadRead, setAheadRead] = useState(false);
  const [monday, setMonday] = useState<Date | null>(null);
  const [events, setEvents] = useState<WeekEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<WeekEvent | null>(null);
  // The find (module 10 U228; pipeline F5): the events whose title or company hold the words stay lit and the rest
  // dim, across the week shown. Nothing is hidden, so the week keeps its shape; the words are kept in the address.
  const [f, setF] = useFilterParams(FIND);
  const lit = useCallback((e: WeekEvent) => matchesWords(f.q, e.title, e.company?.name), [f.q]);

  // BUGS.md D155: a page that sends a reader to one meeting's brief ("Brief me before the call" on a company record) names
  // it in the address, /calendar?event=<its signal>&day=<its start>: the page opens on that meeting's week with its brief
  // open, once; the reader can then move about as ever.
  const asked = useMemo(() => {
    const u = new URLSearchParams(window.location.search);
    const day = u.get('day');
    const at = day ? new Date(day) : null;
    return { event: u.get('event'), at: at && !Number.isNaN(at.getTime()) ? at : null };
  }, []);
  const askedOpened = useRef(false);

  useEffect(() => {
    let cancelled = false;
    calendar.week(store.tenantId, now.toISOString())
      .then((w) => { if (!cancelled) { setAhead(w.events); setMonday(asked.at ? mondayOf(asked.at) : openingMonday(w.events, now)); } })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        // The days ahead failing is not a reason to have no week: fall back to the week holding today.
        setMonday(mondayOf(asked.at ?? now));
      })
      .finally(() => { if (!cancelled) setAheadRead(true); });
    return () => { cancelled = true; };
  }, [store.tenantId, now, asked]);

  const from = monday?.toISOString() ?? null;
  useEffect(() => {
    if (askedOpened.current || !asked.event || !events) return;
    askedOpened.current = true;
    const e = events.find((x) => x.signalId === asked.event);
    if (e) setOpen(e);
  }, [events, asked]);
  useEffect(() => {
    if (!from) return;
    let cancelled = false;
    setEvents(null); setError(null);
    calendar.week(store.tenantId, from)
      .then((w) => { if (!cancelled) setEvents(w.events); })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        setError(e instanceof Error ? e.message : 'Cobrain could not read your week.');
      });
    return () => { cancelled = true; };
  }, [store.tenantId, from]);

  // Both live re-reads replace what is on the page IN PLACE. Emptying first would unmount the week
  // grid, and the grid scrolls inside its own box, so he would be thrown back to 07:00 every time he
  // came back to the window. Neither of them moves which week is open or which day the panel shows:
  // `now` is frozen for the life of the mount on purpose (U95, U97), and a live re-read that moved his
  // week under him would be a change rather than a refresh.
  const shownWeek = useRef<string | null>(null);
  useEffect(() => { shownWeek.current = from; }, [from]);
  const quietWeek = useCallback(() => {
    const asked = from;
    if (!asked) return Promise.resolve();
    return calendar.week(store.tenantId, asked)
      .then((w) => { if (shownWeek.current === asked) setEvents(w.events); })
      .catch((e: unknown) => {
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        // A week already on screen stays on screen: a refresh that failed is not a reason to blank it.
        if (shownWeek.current === asked) setError(e instanceof Error ? e.message : 'Cobrain could not read your week.');
      });
  }, [store.tenantId, from]);
  // The days ahead, for the panel only: openingMonday is deliberately NOT called again here.
  const quietAhead = useCallback(
    () => calendar.week(store.tenantId, now.toISOString()).then((w) => setAhead(w.events), () => {}),
    [store.tenantId, now],
  );
  const live = useCallback(() => Promise.all([quietWeek(), quietAhead()]), [quietWeek, quietAhead]);
  useLiveRefresh(live);

  const move = useCallback((weeks: number) => {
    setMonday((m) => (m ? new Date(m.getFullYear(), m.getMonth(), m.getDate() + weeks * 7) : m));
  }, []);
  const toThisWeek = useCallback(() => setMonday(mondayOf(new Date())), []);

  // The week moves from the keyboard. Guarded like every other page's keys (TasksPage, ItemPage): a
  // person typing in the ask bar is not steering the calendar.
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
      else if (e.key === 't' || e.key === 'T') { e.preventDefault(); toThisWeek(); }
      else if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(null); }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [move, toThisWeek, open]);

  const today = new Date();
  const thisWeek = monday !== null && sameDay(mondayOf(today), monday);

  return (
    <>
      {/* Above both columns, so the week and the brief begin on the same line. Inside `.cal-main` the
          head pushed the grid 41px below the panel and the two never formed a band. */}
      <div className="cal-head">
        <div className="cal-head-l">
          <h1 className="cal-title">{from ? weekWords(from) : 'Your week'}</h1>
          <p className="cal-count">{events === null ? 'Reading your week…' : weekCount(events)}</p>
        </div>
        <div className="cal-move">
          <ActionButton reading type="button" className="opt" onClick={() => move(-1)} disabled={!from} aria-label="The week before">←</ActionButton>
          <ActionButton reading type="button" className={thisWeek ? 'opt on' : 'opt'} onClick={toThisWeek} disabled={!from}>This week</ActionButton>
          <ActionButton reading type="button" className="opt" onClick={() => move(1)} disabled={!from} aria-label="The week after">→</ActionButton>
        </div>
      </div>

      <FilterBar find="Find a meeting this week" q={f.q} onFind={(q) => setF({ q })} picks={[]} onClearAll={() => setF({ q: '' })}
        shown={events === null ? null : events.filter(lit).length} total={events === null ? null : events.length} noun={['event', 'events']} />

      {error ? <p className="cal-error" role="alert">{error}</p> : null}

      <div className={open && roomy ? 'cal-wrap reading' : 'cal-wrap'} ref={wrap}>
        <div className={open ? 'cal-main open' : 'cal-main'}>
          {from
            ? (narrow || (open && roomy)
              ? <WeekList from={from} events={events ?? []} today={today} open={open} onOpen={setOpen} dim={f.q ? (e) => !lit(e) : null} store={store.tenantId} />
              : <WeekGrid from={from} events={events ?? []} today={today} now={now} open={open} onOpen={setOpen} dim={f.q ? (e) => !lit(e) : null} />)
            : null}
        </div>

        {open
          ? <BriefPanel store={store} event={open} onClose={() => setOpen(null)} />
          : <DayAheadPanel store={store} events={ahead} read={aheadRead} now={now} onOpen={setOpen} />}
      </div>
    </>
  );
}

// -------------------------------------------------------------------------------------------
// The week, drawn as a week
// -------------------------------------------------------------------------------------------

interface WeekProps {
  from: string; events: WeekEvent[]; today: Date; open: WeekEvent | null; onOpen: (e: WeekEvent) => void;
  /** While the find holds words: whether an event is one they do not find, drawn dim (F5). */
  dim: ((e: WeekEvent) => boolean) | null;
  /** The store the lines are read in (the day list draws each client meeting's line, U353). */
  store?: string;
}

function WeekGrid({ from, events, today, now, open, onOpen, dim }: WeekProps & { now: Date }) {
  const monday = useMemo(() => new Date(from), [from]);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i)),
    [monday],
  );
  const placed = useMemo(() => place(events, from), [events, from]);
  const allDay = useMemo(() => allDayOf(events, from), [events, from]);
  const hours = Array.from({ length: LAST_HOUR - FIRST_HOUR }, (_, i) => FIRST_HOUR + i);
  // 30px of labelled, tinted, empty stripe was the largest thing on a grid carrying nothing, and it is
  // what clipped the first hour's label. It exists when there is something in it.
  const anyAllDay = allDay.some((d) => d.length > 0);

  // The grid opens on the working day with the night above it, rather than at midnight (U98) — pulled
  // back to an earlier hour when the week holds a call before it, so an early meeting is not a stub
  // half under the day row. It re-opens when the week changes, and not on every render.
  const scroller = useRef<HTMLDivElement>(null);
  const hour = openAtHour(events, from);
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = (hour - FIRST_HOUR) * HOUR_PX; }, [hour, from]);

  // Where the day has got to, on today's column only, moved every minute.
  const [clockNow, setClockNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setClockNow(new Date()), 60_000);
    return () => window.clearInterval(t);
  }, []);
  const todayCol = days.findIndex((d) => sameDay(d, today));
  const nowFrac = (clockNow.getHours() - FIRST_HOUR + clockNow.getMinutes() / 60) / (LAST_HOUR - FIRST_HOUR);
  const showNow = todayCol >= 0 && nowFrac >= 0 && nowFrac <= 1;
  void now;

  return (
    <div className="cal">
      <div className="cal-scroll" ref={scroller}>
        <div className="cal-body">
          <div className="cal-corner" />
          {days.map((d, i) => (
            <div key={d.toISOString()} className={sameDay(d, today) ? 'cal-day today' : 'cal-day'}>
              <span className="cal-dow">{DAY_NAMES[i]}</span> <span className="cal-dom">{d.getDate()}</span>
            </div>
          ))}

          {anyAllDay ? <div className="cal-allday-l">All day</div> : null}
          {anyAllDay ? days.map((d, i) => (
            <div key={`ad-${d.toISOString()}`} className="cal-allday" role="group" aria-label={`All day, ${LONG_DAYS[i]} ${d.getDate()}`}>
              {(allDay[i] ?? []).map((e) => (
                <ActionButton reading
                  type="button" key={e.signalId}
                  className={['cal-chip', e.private ? 'private' : '', dim?.(e) ? 'dim' : ''].filter(Boolean).join(' ')}
                  aria-label={labelOf(e, LONG_DAYS[i])}
                  onClick={() => onOpen(e)}
                >
                  {e.private ? <Lock /> : null}{e.title ?? NO_WORDS}
                </ActionButton>
              ))}
            </div>
          )) : null}

          <div className="cal-hours">
            {hours.map((h) => <div key={h} className="cal-hour"><span>{String(h).padStart(2, '0')}:00</span></div>)}
          </div>
          {days.map((d, i) => (
            <div
              key={`col-${d.toISOString()}`}
              className={sameDay(d, today) ? 'cal-col today' : 'cal-col'}
              role="group" aria-label={`${LONG_DAYS[i]} ${d.getDate()}`}
            >
              {hours.map((h) => <div key={h} className="cal-slot" />)}
              {showNow && i === todayCol
                ? <div className="cal-now" style={{ top: `${nowFrac * 100}%` }} aria-hidden="true"><i /></div>
                : null}
              {placed.filter((p) => p.day === i).map((p) => {
                const e = p.event;
                const isOpen = open?.signalId === e.signalId;
                const cap = captureWords(e.capture);
                const room = blockRoom(p.height, e.company !== null);
                // A box, not a button: the hit area is a button that fills it, so the company's name can be a link of its
                // own to the record above it (BUGS.md X30, U356) without a link inside a button.
                return (
                  <div
                    key={`${e.signalId}-${p.day}`}
                    className={[
                      'cal-block',
                      e.company ? 'client' : '',
                      e.title === null ? 'quiet' : '',
                      e.private ? 'private' : '',
                      room.short ? 'short' : '',
                      isOpen ? 'on' : '',
                      dim?.(e) ? 'dim' : '',
                    ].filter(Boolean).join(' ')}
                    style={{
                      top: `${p.top * 100}%`, height: `${p.height * 100}%`,
                      left: `${(p.lane / p.lanes) * 100}%`, width: `${100 / p.lanes}%`,
                      ['--lines' as string]: String(room.titleLines),
                    }}
                  >
                    <ActionButton reading type="button" className="cal-hit" onClick={() => onOpen(e)} aria-pressed={isOpen} aria-label={labelOf(e, LONG_DAYS[i])} />
                    <span className="cal-when" aria-hidden="true">{clock(e.startsAt)}</span>
                    <span className="cal-what" aria-hidden="true">{e.private ? <Lock /> : null}<span className="cal-name">{e.title ?? NO_WORDS}</span></span>
                    {room.showCompany ? <CompanyName className="cal-who" id={e.company!.id} name={e.company!.name} /> : null}
                    {marksTheBlock(e.capture)
                      ? <span className={`cal-rec ${cap.tone}`} aria-hidden="true"><i className={cap.tone === 'on' ? 'live' : undefined} /></span>
                      : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The week on a phone: the days one under another, each with its meetings as rows (U91's week does not
 * fit in 41px columns). This is what `calendar.css` had described in a comment for a media query that
 * changed nothing, so 10 of 14 titles were clipped on his own week at 375px.
 */
function WeekList({ from, events, today, open, onOpen, dim, store }: WeekProps) {
  const monday = useMemo(() => new Date(from), [from]);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i)),
    [monday],
  );
  const ofDay = (d: Date) => events
    .filter((e) => sameDay(new Date(e.startsAt), d) || (e.allDay && Date.parse(e.endsAt) > d.getTime() && Date.parse(e.startsAt) <= d.getTime()))
    .sort((a, b) => Number(b.allDay) - Number(a.allDay) || Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const lines = useCompanyLines(store, useMemo(() => events.map((e) => e.company?.id), [events]));

  return (
    <div className="cal cal-days">
      {days.map((d, i) => {
        const here = ofDay(d);
        return (
          <section key={d.toISOString()} className={sameDay(d, today) ? 'cal-dayrow today' : 'cal-dayrow'} aria-label={`${LONG_DAYS[i]} ${d.getDate()}`}>
            <h2 className="cal-dayrow-h">
              <span className="cal-dow">{LONG_DAYS[i]}</span> <span className="cal-dom">{d.getDate()}</span>
            </h2>
            {here.length === 0 ? <p className="cal-dayrow-none">Nothing.</p> : null}
            {here.map((e) => {
              const cap = captureWords(e.capture);
              const line = e.company ? lines[e.company.id.toLowerCase()] : undefined;
              return (
                <Fragment key={e.signalId}>
                  <div
                    className={[
                      'cal-row', e.company ? 'client' : '', e.private ? 'private' : '',
                      open?.signalId === e.signalId ? 'on' : '', dim?.(e) ? 'dim' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <ActionButton reading type="button" className="cal-hit" onClick={() => onOpen(e)} aria-pressed={open?.signalId === e.signalId} aria-label={labelOf(e, LONG_DAYS[i])} />
                    <span className="cal-row-t" aria-hidden="true">{e.allDay ? 'All day' : spanWords(e.startsAt, e.endsAt)}</span>
                    <span className="cal-row-w" aria-hidden="true">{e.private ? <Lock /> : null}{e.title ?? NO_WORDS}</span>
                    {e.company ? <CompanyName className="cal-row-c" id={e.company.id} name={e.company.name} /> : null}
                    {marksTheBlock(e.capture) ? <span className={`cal-row-r ${cap.tone}`} aria-hidden="true">{cap.short}</span> : null}
                  </div>
                  {/* The line under each client meeting (U353; the drawing's day list): who they are to us, in the record's words. */}
                  {line ? <div className="cal-row-line"><CompanyLine line={line} go={inPortal} size="sm" /></div> : null}
                </Fragment>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// The brief, and the day ahead
// -------------------------------------------------------------------------------------------

/**
 * What Cobrain will do about recording this meeting (U99).
 *
 * It sits in the head of the brief rather than inside the brief's body, and it reads from the meeting
 * rather than from the brief, for one reason: 21 of his next 28 meetings have nobody from outside
 * iGame on them and so carry no brief at all. A line that lived in the body would be invisible on
 * exactly the meetings he most needs to know about. Reverse: move it into `BriefBody`.
 */
function CaptureLine({ event }: { event: WeekEvent }) {
  const cap = captureWords(event.capture);
  return (
    <p className={`cal-cap ${cap.tone}`}>
      {cap.tone === 'on' ? <i className="live" aria-hidden="true" /> : null}
      {cap.long}
    </p>
  );
}

/** The rundown for one meeting, beside the week (L25; pipeline RC5, U354): the same component the meeting page and the
 *  co-pilot draw, here in full. */
function BriefPanel({ store, event, onClose }: { store: Store; event: WeekEvent; onClose: () => void }) {
  const [view, setView] = useState<RundownView | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Quiet: it replaces the brief in place. Emptying it first would blank the brief he is reading
  // every time he comes back to the window, which is the fault this was meant to cure, not cause.
  const quiet = useCallback(() => {
    setError(null);
    return fetchRundown(store.tenantId, event.company ? { event: event.signalId, org: event.company.id } : { event: event.signalId })
      .then(setView)
      .catch((e: unknown) => {
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        setError(e instanceof Error ? e.message : 'Cobrain could not read the brief.');
      });
  }, [store.tenantId, event.signalId, event.company]);
  const read = useCallback(() => { setView(null); return quiet(); }, [quiet]);

  useEffect(() => { void read(); }, [read]);
  useLiveRefresh(quiet);

  return (
    <aside className="cal-brief" aria-label="The brief for this meeting">
      <div className="cal-brief-head">
        <div>
          <p className="cal-brief-when">{clock(event.startsAt)}–{clock(event.endsAt)}</p>
          <h2 className="cal-brief-title">{event.private ? <Lock /> : null}{event.title ?? NO_WORDS}</h2>
          {event.company && !view?.rundown ? <p className="cal-brief-who"><CompanyName id={event.company.id} name={event.company.name} /></p> : null}
          {event.private ? <p className="cal-private">Private, only you</p> : null}
        </div>
        <ActionButton reading type="button" className="opt" onClick={onClose}>Close</ActionButton>
      </div>
      <CaptureLine event={event} />

      {error ? <p className="cal-error" role="alert">{error} <ActionButton reading type="button" className="dotted" onClick={() => void read()}>Try again</ActionButton></p> : null}
      {view === null && !error ? <p className="cal-brief-wait">Reading what Cobrain holds on them…</p> : null}
      {view?.rundown ? <Rundown data={view.rundown} cut="full" go={inPortal} /> : null}
      {view && !view.rundown ? <p className="cal-brief-none">{view.why}</p> : null}
    </aside>
  );
}

/**
 * The day ahead (U95). Mike, 2026-09-20: "Add a tomorrow panel."
 *
 * The week grid is for placing time; this is for reading before a call. It sits where the brief
 * sits, and shows the next day that has anything on it with each client meeting's brief already
 * open, so the night-before read is one scroll and not a hunt through a grid for tomorrow's column.
 * Clicking one still opens the full brief beside the week, as U91 settled.
 *
 * Its events are the page's one read of the seven days from now — the same read that decides which
 * week the page opens on (U97) — so it stays the day ahead while he is looking at October, and the
 * page does not ask for the same days twice.
 */
function DayAheadPanel({ store, events, read, now, onOpen }: {
  store: Store; events: WeekEvent[] | null; read: boolean; now: Date; onOpen: (e: WeekEvent) => void;
}) {
  const ahead = useMemo(() => (events ? dayAhead(events, now) : null), [events, now]);
  const withClient = ahead ? ahead.events.filter((e) => e.company) : [];

  return (
    <aside className="cal-brief cal-ahead" aria-label="The day ahead">
      <div className="cal-ahead-head">
        <p className="cal-ahead-when">{ahead ? dayWords(ahead.day, now) : 'The day ahead'}</p>
        <h2 className="cal-ahead-title">{ahead ? dayDate(ahead.day) : 'Nothing ahead'}</h2>
      </div>

      {!read ? <p className="cal-brief-wait">Reading the days ahead…</p> : null}
      {read && !events ? <p className="cal-brief-none">Cobrain could not read the days ahead.</p> : null}

      {events !== null && !ahead ? (
        <p className="cal-brief-none">Nothing in your calendar for the next seven days.</p>
      ) : null}

      {ahead && withClient.length === 0 ? (
        <p className="cal-brief-none">{nothingToBriefWords(ahead.events.length)}</p>
      ) : null}

      {ahead?.events.map((e) => (
        <AheadItem key={e.signalId} store={store} event={e} onOpen={onOpen} />
      ))}
    </aside>
  );
}

/** One meeting in the day ahead: when, what, who — and the brief under it when there is one. */
function AheadItem({ store, event, onOpen }: { store: Store; event: WeekEvent; onOpen: (e: WeekEvent) => void }) {
  const [view, setView] = useState<RundownView | null>(null);

  useEffect(() => {
    if (!event.company) return;
    let cancelled = false;
    fetchRundown(store.tenantId, { event: event.signalId, org: event.company.id })
      .then((v) => { if (!cancelled) setView(v); })
      .catch(() => { /* the line still reads; a brief that will not come is not worth a red panel here */ });
    return () => { cancelled = true; };
  }, [store.tenantId, event.signalId, event.company]);

  return (
    <section className={event.company ? 'cal-ahead-item client' : 'cal-ahead-item'}>
      <ActionButton reading type="button" className="cal-ahead-open" onClick={() => onOpen(event)} aria-label={labelOf(event)}>
        <span className="cal-ahead-time">{spanWords(event.startsAt, event.endsAt)}</span>
        <span className="cal-ahead-what">{event.private ? <Lock /> : null}{event.title ?? NO_WORDS}</span>
      </ActionButton>
      {/* The name is a link to the record, so it sits after the button rather than inside it (X30). The rundown below
          heads with the line, which names it again as a link; until it comes, this is the one. */}
      {event.company && !view?.rundown ? <CompanyName className="cal-ahead-who" id={event.company.id} name={event.company.name} /> : null}
      <CaptureLine event={event} />
      {event.company && view?.rundown ? <Rundown data={view.rundown} cut="side" go={inPortal} /> : null}
      {event.company && view && !view.rundown ? <p className="cal-brief-none">{view.why}</p> : null}
      {event.company && !view ? <p className="cal-brief-wait">Reading what Cobrain holds on them…</p> : null}
    </section>
  );
}

/**
 * The brief's body. Exported so `brief-page.test.ts` can render it and assert the rule check 8 of
 * milestone 15 names: no line is SHOWN without its source. The shaping is tested in the API
 * (brief.test.ts), but a source that is assembled and then not drawn is a line without one on the
 * only surface that matters, and nothing in that suite looks at this markup.
 */
export function BriefBody({ brief }: { brief: Brief }) {
  return (
    <div className="cal-brief-body">
      <Section title="What is at stake" lines={brief.stake} lead />
      <Section title="Since you last spoke" lines={brief.since} />
      <Section title="How they are using it" lines={brief.usage} />
      <Section title="Around them" lines={brief.around} />
      {brief.notSeen.length > 0 ? (
        <section className="cal-sec">
          <h3 className="cal-sec-t">What Cobrain could not see</h3>
          <ul className="cal-notseen">{brief.notSeen.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </section>
      ) : null}
    </div>
  );
}

function Section({ title, lines, lead }: { title: string; lines: { text: string; source: string }[]; lead?: boolean }) {
  if (lines.length === 0) return null;
  return (
    <section className={lead ? 'cal-sec lead' : 'cal-sec'}>
      <h3 className="cal-sec-t">{title}</h3>
      {lines.map((l, i) => (
        <p key={i} className="cal-line">
          <span className="cal-line-t">{l.text}</span>
          <span className="cal-line-s">{l.source}</span>
        </p>
      ))}
    </section>
  );
}

void DAY_PX;
